<template>
  <div
    class="popup-gl-code-dropdown popup-inputgroup"
    :class="{
      'in-edit-mode': isInEditMode,
      hovering: !isInEditMode && !disabled && isHovering,
      'cursor-pointer': !disabled,
      disabled: !!disabled,
    }"
    @click="onClick"
    @focusin="onFocus"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <FontAwesomeIcon
      v-if="!disabled"
      aria-hidden="true"
      class="edit-icon"
      :icon="['fas', 'pencil']"
    />

    <Card
      v-if="!disabled"
      :class="{
        'opacity-0 pointer-events-none': !isInEditMode,
        'z-0': !disabled && !isInEditMode,
      }"
      class="popup-gl-code-dropdown-popup popup-inputgroup-card"
      @keydown.tab.passive="onSave"
      @keyup.enter="onSave"
      @keyup.esc="onCancel"
    >
      <template #content>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses }"
          :input-id="id"
          :label="label"
          :errors="v$.internalValue.$errors"
        >
          <RygenDropdown
            :id="id"
            ref="dropdown"
            :model-value="internalValue.code"
            v-bind="{ ...attrsWithoutClass, ...ariaProps }"
            :class="inputClasses"
            :disabled="!!disabled"
            :editable="editable"
            :loading="loading"
            :options="options"
            :placeholder="placeholder"
            append-to="body"
            option-label="code"
            option-value="code"
            filter
            @hide="onDropdownHide"
            @keyup.enter="onEnterInDropdownInput"
            @update:model-value="onUpdate"
          />
        </InputWrapper>

        <div
          class="popup-gl-code-dropdown-popup-buttons popup-inputgroup-buttons"
        >
          <RygenButton
            mode="tertiary"
            :label="t('common.cancel')"
            tabindex="-1"
            @click.stop="onCancel"
          />
          <RygenButton
            mode="primary"
            :disabled="v$.$invalid || loading"
            :label="t('common.done')"
            tabindex="-1"
            @click.stop="onSave"
          />
        </div>
      </template>
    </Card>

    <div
      v-if="isInEditMode"
      class="overlay popup-inputgroup-overlay"
      tabindex="-1"
      @click.stop="onSave"
    >
      &nbsp;
    </div>

    <div
      class="popup-gl-code-dropdown-placeholder popup-inputgroup-placeholder"
    >
      <slot name="placeholder" :label="label" :placeholder="valuePlaceholder">
        <KeyValuePair
          :value="valuePlaceholder"
          :label="label"
          uppercase-label
        />
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { ComponentPublicInstance, PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, useAttrs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { GLCode } from '@rygen/rygen-ui/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('popup-gl-code-dropdown').uid,
  },
  modelValue: {
    type: GLCode,
    default: () => new GLCode(),
  },
  options: {
    type: Array as PropType<GLCode[]>,
    default: () => [],
  },
  label: {
    type: String,
    default: () => '',
  },
  placeholder: {
    type: String,
    default: () => undefined,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  modelValue,
  options,
  label,
  placeholder,
  loading,
  disabled,
  editable,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: GLCode): void;
}>();

defineSlots<{
  placeholder: (props: { label: string; placeholder?: string }) => any;
}>();

const attrs = useAttrs();
const { t } = useI18n();

const internalValue: Ref<GLCode> = ref(new GLCode(modelValue.value));
const wrappedValue = ref({ internalValue });
const rules = {
  internalValue: {
    code: required('glCodes.glCodes'),
  },
};
const { validate, v$ } = useValidations(wrappedValue, rules, id.value);

const isInEditMode = ref(false);
const isHovering = ref(false);
const dropdown: Ref<typeof RygenDropdown | null> = ref(null);

const selectedOption = computed(() => {
  return options.value.find((o) => isEqual(o, internalValue.value));
});

const attrsWithoutClass = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: cssClass, ...newAttrs } = attrs;
  return newAttrs;
});

const valuePlaceholder = computed((): string => {
  if (!selectedOption.value) {
    return placeholder?.value ?? '';
  }

  return selectedOption.value.code ?? '';
});

const onFocus = () => {
  if (!disabled.value) {
    isInEditMode.value = true;
  }
};

const onSave = async () => {
  if (loading.value || !(await validate())) {
    return;
  }

  if (internalValue.value !== modelValue.value) {
    emit('update:model-value', internalValue.value);
  }
  isInEditMode.value = false;
  isHovering.value = false;
};

const onCancel = () => {
  internalValue.value = modelValue.value;
  isInEditMode.value = false;
};

const onClick = () => {
  if (disabled.value) {
    return;
  }

  isInEditMode.value = true;
  const d = dropdown.value as unknown as ComponentPublicInstance;
  nextTick(() => d?.$el?.querySelector?.('input')?.focus());
};

const onDropdownHide = () => {
  const d = dropdown.value as unknown as ComponentPublicInstance;
  nextTick(() => {
    if (isInEditMode.value) {
      d?.$el?.querySelector?.('input')?.focus();
    }
  });
};

const onEnterInDropdownInput = (event: KeyboardEvent) => {
  // Stop the keyboard event from bubbling up to the popup card so that the
  // popup isn't closed.
  event.stopPropagation();
};

const onUpdate = (newVal?: string) => {
  if (!newVal) {
    return;
  }

  internalValue.value.code = newVal;
};

watch(
  modelValue,
  (newVal: GLCode) => {
    internalValue.value = new GLCode(newVal);
  },
  { deep: true, immediate: true },
);

watch(isInEditMode, (newVal: boolean) => {
  if (!newVal) {
    dropdown.value?.hide();
  }
});
</script>

<style>
.popup-gl-code-dropdown.popup-inputgroup {
  &:not(.hovering) .edit-icon {
    color: transparent;
  }

  .edit-icon {
    position: absolute;
    top: calc(var(--sp-6) * -1);
    right: calc(var(--sp-6) * -1);
    transition: color 100ms ease-in;
  }

  & > .p-card {
    left: calc(var(--sp-6) * -1);
    width: max-content;
  }
}
</style>
